import { useState, useEffect } from "react";
import { RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { isServiceWorkerStale, clearAllCachesAndRefresh } from "@/utils/cacheUtils";
import { BUILD_HASH } from "@/config/version";

interface ShareTroubleshootProps {
  compact?: boolean;
  showVersion?: boolean;
}

export function ShareTroubleshoot({ compact = false, showVersion = false }: ShareTroubleshootProps) {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check for pending SW update when component mounts
  useEffect(() => {
    const checkUpdate = async () => {
      const stale = await isServiceWorkerStale();
      setHasUpdate(stale);
    };
    checkUpdate();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await clearAllCachesAndRefresh();
    // Note: page will refresh, so no need to reset state
  };

  // Version badge component
  const VersionBadge = () => (
    <span className="text-[10px] text-muted-foreground/60 font-mono">
      v{BUILD_HASH.slice(0, 6)}
    </span>
  );

  // Show prominent banner if update is pending
  if (hasUpdate) {
    return (
      <Alert className="border-primary/50 bg-primary/10">
        <AlertCircle className="h-4 w-4 text-primary" />
        <AlertDescription className="flex items-center justify-between gap-2">
          <span className="text-sm">Update available for better sharing</span>
          <Button 
            size="sm" 
            variant="default"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="shrink-0"
          >
            {isRefreshing ? (
              <RefreshCw className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-3 w-3 mr-1" />
            )}
            Update Now
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Show subtle "Not working?" link
  if (compact) {
    return (
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors disabled:opacity-50"
        >
          {isRefreshing ? "Updating..." : "Not working? Tap to update"}
        </button>
        {showVersion && <VersionBadge />}
      </div>
    );
  }

  return (
    <div className="text-center pt-2 border-t border-border/50">
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors inline-flex items-center gap-1 disabled:opacity-50"
        >
          {isRefreshing ? (
            <>
              <RefreshCw className="h-3 w-3 animate-spin" />
              Updating...
            </>
          ) : (
            "Sharing not working? Tap to update"
          )}
        </button>
        {showVersion && <VersionBadge />}
      </div>
    </div>
  );
}
